import React, { createContext, useCallback, useContext, useState } from 'react';
import { financeAPI } from '../services/api';

// Tracks the finance module's MFA state client-side, mirroring the backend's
// FinanceMFADevice/FinanceSessionState. Every finance API call is gated on
// this — see components/finance/FinanceMFAGate.js, which is what actually
// renders the provision/confirm/verify flow using the functions here.

const FinanceContext = createContext(null);

export const useFinance = () => {
  const context = useContext(FinanceContext);
  if (!context) {
    throw new Error('useFinance must be used within FinanceProvider');
  }
  return context;
};

export const FinanceProvider = ({ children }) => {
  // null = not checked yet; otherwise { enrolled, session_mfa_fresh }
  const [mfaStatus, setMfaStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusError, setStatusError] = useState(null);

  const refreshMfaStatus = useCallback(async () => {
    setStatusLoading(true);
    setStatusError(null);
    try {
      const res = await financeAPI.mfa.status();
      setMfaStatus(res.data);
      return res.data;
    } catch (e) {
      setStatusError(e?.response?.data?.error || 'Failed to check finance MFA status.');
      throw e;
    } finally {
      setStatusLoading(false);
    }
  }, []);

  // Only ever called from a screen that has already confirmed (via
  // mfaStatus.enrolled === false) that no device exists yet — provisioning
  // an already-confirmed device would regenerate its secret and break the
  // user's existing authenticator pairing.
  const provisionDevice = useCallback(async () => {
    const res = await financeAPI.mfa.provision();
    return res.data; // { secret, provisioning_uri }
  }, []);

  const confirmDevice = useCallback(async (code) => {
    await financeAPI.mfa.confirm(code);
    await refreshMfaStatus();
  }, [refreshMfaStatus]);

  const verifySession = useCallback(async (code) => {
    await financeAPI.mfa.verify(code);
    await refreshMfaStatus();
  }, [refreshMfaStatus]);

  const value = {
    mfaStatus,
    statusLoading,
    statusError,
    refreshMfaStatus,
    provisionDevice,
    confirmDevice,
    verifySession,
  };

  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>;
};
